import { Box, Divider, Flex } from '@chakra-ui/layout';
import {
  type CollisionDetection,
  DndContext,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { type RefObject, useCallback, useEffect, useState } from 'react';

import { FeatureFlag, useResizeObserver } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { useGridLayout } from '../../hooks/useGridLayout';
import { AIAssistantDrawer } from '../AIAssistant/AIAssistantDrawer';
import { AIAssistantFAB } from '../AIAssistant/AIAssistantFAB';
import { Chart } from '../Chart/ChartRoot/Chart';
import { ChartBuilderPanel } from '../ChartConfig/ChartBuilderPanel';
import { useEditMode } from '../EditModeProvider/EditModeProvider';
import { DEFAULT_GRID_ROWS } from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { TabContentAction } from '../PageConfigProvider/page-reducer';
import { ReportCoverPage } from '../Reports/ReportCoverPage';
import { ReportFooterPage } from '../Reports/ReportFooterPage';
import { GhostPlaceholders } from './components/GhostPlaceholders';
import { RESIZE_HANDLE_PADDING } from './constants';
import { Draggable } from './Draggable';
import styles from './EditableGrid.module.css';
import { EditGrid } from './EditGrid';
import { GridActionButton } from './GridActionButton';
import { useGrid } from './GridProvider';
import { getEntityTypeFromPrimaryFilter } from './getEntityTypeFromPrimaryFilter';
import { useEditableGrid } from './hooks/useEditableGrid';
import { calculateReportGhosts } from './utils/ghost-layout-generator.reports';

const customCollisionDetectionAlgorithm: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return rectIntersection(args);
};

interface ReportEditableGridProps {
  onBuilderOpenChange?: (isOpen: boolean) => void;
  onAIDrawerOpenChange?: (isOpen: boolean) => void;
  gridContainerRef: RefObject<HTMLDivElement>;
}

export const ReportEditableGrid = ({
  onBuilderOpenChange,
  onAIDrawerOpenChange,
  gridContainerRef,
}: ReportEditableGridProps) => {
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });
  const sensors = useSensors(pointerSensor);

  const { gridItems, grid, edgeDragging, itemDragging } = useGrid();
  const { height: gridContainerHeight } = useResizeObserver(gridContainerRef);
  const {
    primaryFilter,
    dispatch,
    config: tabContentConfig,
  } = useTabContentConfig();
  const { title, description } = tabContentConfig;
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const { editMode } = useEditMode();
  const isAIReportGenEnabled = useUnleashFlag(FeatureFlag.AIPageBuilder);

  const reportEntityType = getEntityTypeFromPrimaryFilter(primaryFilter);
  const canUseReportAI = reportEntityType !== null && editMode;

  const gridLayout = useGridLayout({
    isReport: true,
    gridRows: grid.rows,
    gridColumns: grid.columns,
  });

  const {
    openConfigId,
    closeConfig,
    selectedIds,
    clearSelection,
    handleChartClick,
    filteredGridItems,
    selectedItem,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
  } = useEditableGrid({ onBuilderOpenChange });

  const handleAIDrawerOpen = () => {
    if (openConfigId) {
      closeConfig();
    }
    setIsAIDrawerOpen(true);
    onAIDrawerOpenChange?.(true);
  };

  // Close AI drawer when chart edit panel opens
  useEffect(() => {
    if (openConfigId && isAIDrawerOpen) {
      setIsAIDrawerOpen(false);
      onAIDrawerOpenChange?.(false);
    }
  }, [openConfigId, isAIDrawerOpen, onAIDrawerOpenChange]);

  const [dragTransform, setDragTransform] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const onDragEnd = useCallback(
    (event: Parameters<typeof handleDragEnd>[0]) => {
      handleDragEnd(event);
      setDragTransform(null);
    },
    [handleDragEnd]
  );

  const numPages = Math.floor(grid.rows / DEFAULT_GRID_ROWS);
  const footerPageStartY = (numPages - 1) * DEFAULT_GRID_ROWS;

  const ghostPlaceholders = calculateReportGhosts({
    items: gridItems,
    grid: { rows: grid.rows, columns: grid.columns },
  });

  const visibleGhostPlaceholders =
    ghostPlaceholders.length > 0
      ? ghostPlaceholders.filter(
          (ghost) =>
            ghost.position.y >= DEFAULT_GRID_ROWS &&
            ghost.position.y < footerPageStartY
        )
      : ghostPlaceholders;

  const handleAddPage = (pageNumber: number) => {
    dispatch({ type: 'ADD_PAGE', payload: { pageNumber } });
  };
  const handleDeletePageAbove = (pageNumber: number) => {
    dispatch({
      type: 'DELETE_PAGE_ABOVE',
      payload: { pageNumber },
    } as unknown as TabContentAction);
  };
  const isFooterPage = (pageNum: number) => pageNum === numPages;

  const pageDividers = Array.from({ length: numPages }).map((_, index) => {
    const pageNum = index + 1;
    const transformY = (() => {
      if (pageNum === numPages) return 'calc(-100% - 1rem)';
      return '-50%';
    })();

    if (isFooterPage(pageNum)) {
      return (
        <Box
          key={pageNum}
          position="absolute"
          top={`${pageNum * (gridContainerHeight / numPages)}px`}
          left="0"
          right="0"
        >
          <Divider />
        </Box>
      );
    }

    return (
      <Box
        key={pageNum}
        position="absolute"
        top={`${pageNum * (gridContainerHeight / numPages)}px`}
        left="0"
        right="0"
      >
        <Divider />
        <GridActionButton
          label="+ Add Page"
          onClick={() => handleAddPage(pageNum)}
          groupProps={{
            position: 'absolute',
            left: '50%',
            transform: `translateX(-50%) translateY(${transformY})`,
            width: '140px',
            backgroundColor: 'white',
            zIndex: 1,
          }}
          additionalActions={[
            {
              label: 'Delete Page',
              onClick: () => handleDeletePageAbove(pageNum),
              isDisabled: numPages <= 1,
            },
          ]}
          menuButtonProps={{
            'aria-label': 'More options',
            height: '100%',
            borderRightRadius: 'full',
            backgroundColor: 'white',
          }}
          menuPlacement="bottom-end"
        />
      </Box>
    );
  });

  const scrollSx = {
    overflowAnchor: 'none',
    scrollbarGutter: 'stable',
    overscrollBehavior: 'contain',
    scrollbarWidth: 'thin',
    scrollbarColor: '#E3E3E3 transparent',
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
      borderRadius: '10px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      backgroundColor: '#E3E3E3',
    },
  };

  return (
    <Flex direction="column" height="100%" minHeight="0">
      {isAIReportGenEnabled && canUseReportAI && reportEntityType && (
        <>
          <AIAssistantFAB
            onClick={handleAIDrawerOpen}
            isVisible={!isAIDrawerOpen}
          />
          <AIAssistantDrawer
            isOpen={isAIDrawerOpen}
            onClose={() => {
              setIsAIDrawerOpen(false);
              onAIDrawerOpenChange?.(false);
            }}
            mode="report"
            entityType={reportEntityType}
          />
        </>
      )}
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        collisionDetection={customCollisionDetectionAlgorithm}
        modifiers={[restrictToParentElement]}
        sensors={sensors}
        autoScroll={false}
      >
        <Box
          flex="1"
          minHeight="0"
          position="relative"
          overflow={gridLayout.styles.overflow}
          sx={scrollSx}
        >
          {(itemDragging || edgeDragging) && (
            <EditGrid
              columns={grid.columns}
              rows={grid.rows}
              width={grid.width}
              height={grid.height}
              paddingLeft={grid.paddingLeft}
              paddingTop={grid.paddingTop}
              trackWidth={grid.trackWidth}
              trackHeight={grid.trackHeight}
              columnGap={grid.columnGap}
              rowGap={grid.rowGap}
            />
          )}
          <Box
            id="editable-grid-container"
            ref={gridContainerRef}
            display="grid"
            position="relative"
            zIndex={1}
            gap="1rem"
            width="100%"
            height={gridLayout.contentHeight}
            minWidth="0"
            minHeight="0"
            gridTemplateRows={gridLayout.styles.gridTemplateRows}
            gridTemplateColumns={gridLayout.styles.gridTemplateColumns}
            padding={RESIZE_HANDLE_PADDING}
            paddingLeft={gridLayout.styles.paddingLeft}
            paddingRight={gridLayout.styles.paddingRight}
            backgroundColor="white"
            className={styles.grid}
            overflow="hidden"
          >
            <Box
              width="100%"
              height={`calc(100% / ${numPages})`}
              position="absolute"
              zIndex={-1}
            >
              <ReportCoverPage
                title={title ?? ''}
                description={description ?? ''}
                isEditMode={true}
              />
            </Box>
            <Box
              width="100%"
              height={`calc(100% / ${numPages})`}
              position="absolute"
              top={`calc(100% - (100% / ${numPages}))`}
              zIndex={-1}
            >
              <ReportFooterPage />
            </Box>
            <GhostPlaceholders
              ghosts={visibleGhostPlaceholders}
              onAddItem={handleAddItem}
            />
            {filteredGridItems.map((item) => (
              <Draggable
                id={item.id}
                key={item.id}
                item={item}
                focused={item.id === openConfigId}
                selected={selectedIds.has(item.id)}
                dragTransform={dragTransform}
                setDragTransform={setDragTransform}
                selectedIds={selectedIds}
                style={{
                  gridColumn: `${item.position.x + 1} / span ${item.position.width}`,
                  gridRow: `${item.position.y + 1} / span ${item.position.height}`,
                }}
              >
                <Chart
                  item={item}
                  primaryFilter={primaryFilter}
                  onClick={(e) => handleChartClick(item.id, e)}
                  {...((!!itemDragging || !!edgeDragging) && {
                    pointerEvents: 'none',
                  })}
                  position="relative"
                  variant="report"
                />
              </Draggable>
            ))}
            {pageDividers}

            <ChartBuilderPanel
              isOpen={!!openConfigId}
              item={selectedItem}
              selectedIds={selectedIds}
              clearSelection={clearSelection}
              onClose={closeConfig}
            />
          </Box>
        </Box>
      </DndContext>
    </Flex>
  );
};
